import React, { useMemo, useState } from 'react'
import { Form, Icon, Message, Popup } from 'semantic-ui-react'
import { Field, useForm, useFormState } from 'react-final-form'
import { FieldArray } from 'react-final-form-arrays'
import { ArrayFieldLabelStyle } from '../styles'
import { AliasRemapButton } from './AliasRemapButton'
import { Duplicates } from '../../../utils/aliases/Duplicates'
import { Alias } from '../../../redux/types/products'

export const Aliases = ({ enableAliasRemap }: { enableAliasRemap?: boolean }) => {
  const [aliasText, setAliasText] = useState<string>('')
  const { initialValues, values } = useFormState()
  const {
    mutators: { push, update },
  } = useForm()

  const duplicates = useMemo(() => {
    return new Duplicates(values.aliases_attributes)
  }, [values.aliases_attributes])

  const addAlias = (alias: string) => {
    const trimmedAlias = alias.trim()

    if (trimmedAlias === '') return
    if (duplicates.includes({ name: trimmedAlias })) return

    push('aliases_attributes', { name: trimmedAlias })
    setAliasText('')
  }

  const remappable = (alias: Alias) => {
    return enableAliasRemap && alias.id && !duplicates.isPaddedDuplicate(alias)
  }

  return (
    <>
      <ArrayFieldLabelStyle>Product aliases</ArrayFieldLabelStyle>
      <Form.Field>
        <Form.Input
          type="text"
          value={aliasText}
          onChange={(e) => {
            setAliasText(e.currentTarget.value)
          }}
          onBlur={() => setAliasText(aliasText.trim())}
          onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              addAlias(aliasText)
            }
          }}
          placeholder="New alias"
        />
      </Form.Field>
      <Form.Button
        fluid
        type="button"
        onClick={(e) => {
          e.preventDefault()
          addAlias(aliasText)
        }}
      >
        <Icon name="add" size="small" />
        Add new alias
      </Form.Button>
      <Form.Field
        style={{
          minHeight: 120,
          maxHeight: 550,
          overflow: 'auto',
          outline: 'lightgray auto thin',
          padding: '10px',
        }}
      >
        {initialValues?.removal_status != null ? (
          <Message warning size="small" style={{ display: 'block' }}>
            {initialValues?.removal_status === 'removed'
              ? 'This product is removed.'
              : 'This product is currently discontinued and pending removal.'}{' '}
            Do not add new aliases.
          </Message>
        ) : null}
        <FieldArray name="aliases_attributes" key={values.aliases_attributes}>
          {({ fields }) =>
            fields.map((name, index) => (
              <Field name={`${name}[name]`} key={name}>
                {(props) => (
                  <Form.Field inline style={{ display: 'flex' }}>
                    <Message
                      style={{
                        display: 'inline-block',
                        width: '100%',
                        whiteSpace: 'pre-wrap',
                        textDecorationLine: fields.value[index]['_destroy']
                          ? 'line-through'
                          : 'none',
                      }}
                    >
                      <p>{props.input.value}</p>
                    </Message>
                    <div style={{ display: 'flex' }}>
                      {duplicates.isPaddedDuplicate(fields.value[index]) ? (
                        <Popup
                          content="This is a duplicate alias!"
                          trigger={<Icon bordered color="yellow" name="exclamation circle" />}
                        />
                      ) : (
                        <Popup
                          content={fields.value[index]['_destroy'] ? 'Keep alias' : 'Delete alias'}
                          trigger={
                            <Icon
                              link
                              bordered
                              name={
                                fields.value[index]['_destroy'] ? 'undo' : 'trash alternate outline'
                              }
                              onClick={() => {
                                fields.value[index]['id'] != null
                                  ? update('aliases_attributes', index, {
                                      ...fields.value[index],
                                      _destroy: !fields.value[index]['_destroy'],
                                    })
                                  : fields.remove(index)
                              }}
                            />
                          }
                        />
                      )}
                      {remappable(fields.value[index]) ? (
                        <AliasRemapButton
                          productId={initialValues.id}
                          aliasId={fields.value[index].id}
                        />
                      ) : null}
                    </div>
                  </Form.Field>
                )}
              </Field>
            ))
          }
        </FieldArray>
      </Form.Field>
    </>
  )
}
